import { useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { AlertTriangle, CheckCircle2, PlayCircle, RefreshCw, Rocket, } from "lucide-react"
import { AdminLayout } from "@/components/layouts/admin-layout"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle, } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from "@/components/ui/dialog"
import { PageHeader } from "@/components/ui/headers"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from "@/components/ui/table"
import { TableSection } from "@/components/ui/table-section"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { t } from "@/lib/i18n"
type FlashMessage = string | {
    header?: string
    description?: string
}
type SchemaSummary = {
    table: string
    purpose: string
    available?: boolean
}
type Overview = {
    totalJobs: number
    queuedJobs: number
    runningJobs: number
    scheduledJobs: number
    blockedJobs: number
    failedJobs: number
    finishedJobs: number
    pausedQueues: number
    activeProcesses: number
    staleProcesses: number
    jobsCreatedLast24h: number
    failedLast24h: number
}
type QueueStat = {
    queueName: string
    queued: number
    running: number
    scheduled: number
    blocked: number
    failed: number
    paused: boolean
}
type RecentJob = {
    id: number
    activeJobId: string | null
    className: string
    queueName: string
    priority: number
    status: string
    createdAt: string
    scheduledAt: string | null
    finishedAt: string | null
    failedMessage: string | null
    processId: number | null
    concurrencyKey: string | null
}
type QueuedExecution = {
    id: number
    className: string
    queueName: string
    priority: number
    status: string
    scheduledAt: string | null
    enqueuedAt: string
}
type FailedExecution = {
    id: number
    className: string
    queueName: string
    failedAt: string
    message: string | null
    exceptionClass: string | null
    backtrace: string[]
}
type ProcessRow = {
    id: number
    name: string
    kind: string
    pid: number
    hostname: string | null
    supervisorId: number | null
    lastHeartbeatAt: string
    stale: boolean
}
type RecurringTask = {
    key: string
    className: string | null
    queueName: string | null
    priority: number | null
    schedule: string
    static: boolean
    description: string | null
    lastEnqueuedAt: string | null
    nextRunAt: string | null
}
type AvailableJob = {
    className: string
    queueName: string
    namespace: string
    performSignature: string
}
type Props = {
    generatedAt: string
    databaseAvailable: boolean
    errorMessage?: string
    missingTables?: string[]
    schemaSummary: SchemaSummary[]
    overview: Overview
    queues: QueueStat[]
    recentJobs: RecentJob[]
    queuedExecutions: QueuedExecution[]
    failedExecutions: FailedExecution[]
    processes: ProcessRow[]
    recurringTasks: RecurringTask[]
    availableJobs: AvailableJob[]
    flash?: {
        notice?: FlashMessage
        alert?: FlashMessage
    }
}
function formatDateTime(value: string | null) {
    if (!value)
        return "-"
    return new Intl.DateTimeFormat("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(new Date(value))
}
function normalizeFlash(message?: FlashMessage) {
    if (!message)
        return null
    if (typeof message === "string") {
        return { title: message, description: null }
    }
    return {
        title: message.header || "",
        description: message.description || null,
    }
}
function statusBadge(status: string) {
    const normalizedStatus = status.toLowerCase()
    if (["failed"].includes(normalizedStatus)) {
        return <Badge variant="destructive">{t("ui.react.pages.admin.jobs.index.failed")}</Badge>
    }
    if (["finished"].includes(normalizedStatus)) {
        return <Badge variant="secondary">{t("ui.react.pages.admin.jobs.index.finished")}</Badge>
    }
    if (["claimed", "running"].includes(normalizedStatus)) {
        return <Badge variant="default">{t("ui.react.pages.admin.jobs.index.running")}</Badge>
    }
    if (["ready", "queued"].includes(normalizedStatus)) {
        return <Badge variant="outline">{t("ui.react.pages.admin.jobs.index.queue")}</Badge>
    }
    if (["scheduled"].includes(normalizedStatus)) {
        return <Badge variant="outline">{t("ui.react.pages.admin.jobs.index.scheduled")}</Badge>
    }
    if (["blocked"].includes(normalizedStatus)) {
        return <Badge variant="outline">{t("ui.react.pages.admin.jobs.index.blocked")}</Badge>
    }
    return <Badge variant="outline">{status}</Badge>
}
export default function AdminJobsIndex(props: Props) {
    const flash = props.flash || {}
    const [dialogOpen, setDialogOpen] = useState(false)
    const [selectedJob, setSelectedJob] = useState<AvailableJob | null>(null)
    const [argumentsJson, setArgumentsJson] = useState("{}")
    const notice = normalizeFlash(flash.notice)
    const alert = normalizeFlash(flash.alert)
    const lastUpdated = useMemo(() => formatDateTime(props.generatedAt), [props.generatedAt])
    const openRunDialog = (job: AvailableJob) => {
        setSelectedJob(job)
        setArgumentsJson("{}")
        setDialogOpen(true)
    }
    const runJob = () => {
        if (!selectedJob)
            return
        router.post("/jobs", {
            class_name: selectedJob.className,
            arguments: argumentsJson.trim(),
        }, {
            preserveScroll: true,
            onSuccess: () => {
                setDialogOpen(false)
                setSelectedJob(null)
            },
        })
    }
    const refreshDashboard = () => {
        router.reload()
    }
    return (<AdminLayout>
      <MainContainer size="fullWidth">
        <PageHeader title={t("ui.react.pages.admin.jobs.index.jobs")} description={`Panel operativo de Solid Queue. Última actualización: ${lastUpdated}`} actions={(<Button type="button" variant="outline" size="sm" onClick={refreshDashboard}>
              <RefreshCw className="size-4"/>{t("ui.react.pages.admin.jobs.index.update")}</Button>)}/>

        {notice && (<Alert>
            <CheckCircle2 className="h-4 w-4"/>
            <AlertTitle>{notice.title}</AlertTitle>
            {notice.description && <AlertDescription>{notice.description}</AlertDescription>}
          </Alert>)}

        {alert && (<Alert variant="destructive">
            <AlertTriangle className="h-4 w-4"/>
            <AlertTitle>{alert.title || "Error"}</AlertTitle>
            {alert.description && <AlertDescription>{alert.description}</AlertDescription>}
          </Alert>)}

        {!props.databaseAvailable && (<Alert variant="destructive">
            <AlertTriangle className="h-4 w-4"/>
            <AlertTitle>{t("ui.react.pages.admin.jobs.index.no_could_read_tables_solid")}</AlertTitle>
            <AlertDescription>
              {props.errorMessage || t("ui.admin.jobs.errors.queue_database_unavailable")}
            </AlertDescription>
          </Alert>)}

        {props.databaseAvailable && (props.missingTables?.length || 0) > 0 && (<Alert>
            <AlertTriangle className="h-4 w-4"/>
            <AlertTitle>{t("ui.react.pages.admin.jobs.index.some_tables_no_available")}</AlertTitle>
            <AlertDescription>
              {props.missingTables?.join(", ")}
            </AlertDescription>
          </Alert>)}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardHeader className="pb-0">
              <CardDescription>{t("ui.react.pages.admin.jobs.index.total_jobs")}</CardDescription>
              <CardTitle className="text-2xl">{props.overview.totalJobs}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 text-xs text-muted-foreground">{t("ui.react.pages.admin.jobs.index.latest_24h")}{props.overview.jobsCreatedLast24h}</CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-0">
              <CardDescription>{t("ui.react.pages.admin.jobs.index.queue")}</CardDescription>
              <CardTitle className="text-2xl">{props.overview.queuedJobs}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 text-xs text-muted-foreground">{t("ui.react.pages.admin.jobs.index.scheduled_3")}{props.overview.scheduledJobs}{t("ui.react.pages.admin.jobs.index.blocked_3")}{props.overview.blockedJobs}</CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-0">
              <CardDescription>{t("ui.react.pages.admin.jobs.index.execution")}</CardDescription>
              <CardTitle className="text-2xl">{props.overview.runningJobs}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 text-xs text-muted-foreground">{t("ui.react.pages.admin.jobs.index.workers_active")}{props.overview.activeProcesses}</CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-0">
              <CardDescription>{t("ui.react.pages.admin.jobs.index.failures")}</CardDescription>
              <CardTitle className="text-2xl">{props.overview.failedJobs}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 text-xs text-muted-foreground">{t("ui.react.pages.admin.jobs.index.latest_24h_2")}{props.overview.failedLast24h}</CardContent>
          </Card>
        </section>

        <Tabs defaultValue="operations">
          <TabsList>
            <TabsTrigger value="operations">{t("ui.react.pages.admin.jobs.index.operation")}</TabsTrigger>
            <TabsTrigger value="queues">{t("ui.react.pages.admin.jobs.index.queues")}</TabsTrigger>
            <TabsTrigger value="failures">{t("ui.react.pages.admin.jobs.index.failed_2")}</TabsTrigger>
            <TabsTrigger value="recurring">{t("ui.react.pages.admin.jobs.index.recurrences")}</TabsTrigger>
            <TabsTrigger value="manual">{t("ui.react.pages.admin.jobs.index.run_job")}</TabsTrigger>
          </TabsList>

          <TabsContent value="operations" className="space-y-6">
            <TableSection title={t("ui.react.pages.admin.jobs.index.jobs_recent")} description={`${t("ui.react.pages.admin.jobs.index.latest")}${props.recentJobs.length}${t("ui.react.pages.admin.jobs.index.jobs_created_solid_queue")}`}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.job")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.queue_2")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.status")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.created")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.scheduled")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.finished")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {props.recentJobs.length === 0 && (<TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">{t("ui.react.pages.admin.jobs.index.no_recent_jobs")}</TableCell>
                        </TableRow>)}
                      {props.recentJobs.map((job) => (<TableRow key={job.id}>
                          <TableCell className="font-mono text-xs">{job.id}</TableCell>
                          <TableCell>
                            <div className="font-medium">{job.className}</div>
                            {job.failedMessage && (<p className="text-xs text-destructive truncate max-w-[360px]">
                                {job.failedMessage}
                              </p>)}
                          </TableCell>
                          <TableCell>{job.queueName}</TableCell>
                          <TableCell>{statusBadge(job.status)}</TableCell>
                          <TableCell>{formatDateTime(job.createdAt)}</TableCell>
                          <TableCell>{formatDateTime(job.scheduledAt)}</TableCell>
                          <TableCell>{formatDateTime(job.finishedAt)}</TableCell>
                        </TableRow>))}
                    </TableBody>
                  </Table>
            </TableSection>

            <TableSection title={t("ui.react.pages.admin.jobs.index.executions_pending")} description={t("ui.react.pages.admin.jobs.index.jobs_ready_scheduled_blocked_execution")}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.job")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.queue_2")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.priority")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.status")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.queued")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.scheduled")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {props.queuedExecutions.length === 0 && (<TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">{t("ui.react.pages.admin.jobs.index.no_pending_executions")}</TableCell>
                        </TableRow>)}
                      {props.queuedExecutions.map((execution) => (<TableRow key={`${execution.status}-${execution.id}`}>
                          <TableCell className="font-medium">{execution.className}</TableCell>
                          <TableCell>{execution.queueName}</TableCell>
                          <TableCell>{execution.priority}</TableCell>
                          <TableCell>{statusBadge(execution.status)}</TableCell>
                          <TableCell>{formatDateTime(execution.enqueuedAt)}</TableCell>
                          <TableCell>{formatDateTime(execution.scheduledAt)}</TableCell>
                        </TableRow>))}
                    </TableBody>
                  </Table>
            </TableSection>
          </TabsContent>

          <TabsContent value="queues" className="space-y-6">
            <TableSection title={t("ui.react.pages.admin.jobs.index.status_queue")} description={t("ui.react.pages.admin.jobs.index.view_operational_backlog_execution_pauses")}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.queue_2")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.queue")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.running")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.scheduled_2")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.blocked_2")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.failed_2")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.status")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {props.queues.length === 0 && (<TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">{t("ui.react.pages.admin.jobs.index.no_queue_data")}</TableCell>
                        </TableRow>)}
                      {props.queues.map((queue) => (<TableRow key={queue.queueName}>
                          <TableCell className="font-medium">{queue.queueName}</TableCell>
                          <TableCell>{queue.queued}</TableCell>
                          <TableCell>{queue.running}</TableCell>
                          <TableCell>{queue.scheduled}</TableCell>
                          <TableCell>{queue.blocked}</TableCell>
                          <TableCell>{queue.failed}</TableCell>
                          <TableCell>
                            {queue.paused ? <Badge variant="destructive">{t("ui.react.pages.admin.jobs.index.paused")}</Badge> : <Badge variant="secondary">{t("ui.react.pages.admin.jobs.index.active")}</Badge>}
                          </TableCell>
                        </TableRow>))}
                    </TableBody>
                  </Table>
            </TableSection>

            <TableSection title={t("ui.react.pages.admin.jobs.index.processes_queue")} description={t("ui.react.pages.admin.jobs.index.heartbeat_workers_dispatchers")}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.process")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.kind")}</TableHead>
                        <TableHead>PID</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.host")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.last_heartbeat")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.status")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {props.processes.length === 0 && (<TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">{t("ui.react.pages.admin.jobs.index.no_registered_processes")}</TableCell>
                        </TableRow>)}
                      {props.processes.map((process) => (<TableRow key={process.id}>
                          <TableCell className="font-medium">{process.name}</TableCell>
                          <TableCell>{process.kind}</TableCell>
                          <TableCell>{process.pid}</TableCell>
                          <TableCell>{process.hostname || "-"}</TableCell>
                          <TableCell>{formatDateTime(process.lastHeartbeatAt)}</TableCell>
                          <TableCell>
                            {process.stale ? <Badge variant="destructive">{t("ui.react.pages.admin.jobs.index.heartbeat")}</Badge> : <Badge variant="secondary">{t("ui.react.pages.admin.jobs.index.healthy")}</Badge>}
                          </TableCell>
                        </TableRow>))}
                    </TableBody>
                  </Table>
            </TableSection>
          </TabsContent>

          <TabsContent value="failures" className="space-y-6">
            <TableSection title={t("ui.react.pages.admin.jobs.index.latest_failures")} description={t("ui.react.pages.admin.jobs.index.errors_recent_saved_solid_queue")}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.job")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.queue_2")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.exception")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.message")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.date")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {props.failedExecutions.length === 0 && (<TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">{t("ui.react.pages.admin.jobs.index.no_failed_executions")}</TableCell>
                        </TableRow>)}
                      {props.failedExecutions.map((failure) => (<TableRow key={failure.id}>
                          <TableCell className="font-medium">{failure.className}</TableCell>
                          <TableCell>{failure.queueName}</TableCell>
                          <TableCell>{failure.exceptionClass || "-"}</TableCell>
                          <TableCell className="max-w-[400px] truncate">{failure.message || t("ui.admin.jobs.no_message")}</TableCell>
                          <TableCell>{formatDateTime(failure.failedAt)}</TableCell>
                        </TableRow>))}
                    </TableBody>
                  </Table>
            </TableSection>
          </TabsContent>

          <TabsContent value="recurring" className="space-y-6">
            <TableSection title={t("ui.react.pages.admin.jobs.index.tasks_recurring")} description={t("ui.react.pages.admin.jobs.index.definitions_active_registered_solid_queue")}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.key")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.job")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.schedule")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.queue_2")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.last_run")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.next_run")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {props.recurringTasks.length === 0 && (<TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">{t("ui.react.pages.admin.jobs.index.no_recurring_tasks")}</TableCell>
                        </TableRow>)}
                      {props.recurringTasks.map((task) => (<TableRow key={task.key}>
                          <TableCell className="font-medium">{task.key}</TableCell>
                          <TableCell>{task.className || "Comando"}</TableCell>
                          <TableCell className="font-mono text-xs">{task.schedule}</TableCell>
                          <TableCell>{task.queueName || "default"}</TableCell>
                          <TableCell>{formatDateTime(task.lastEnqueuedAt)}</TableCell>
                          <TableCell>{formatDateTime(task.nextRunAt)}</TableCell>
                        </TableRow>))}
                    </TableBody>
                  </Table>
            </TableSection>

            <TableSection title={t("ui.react.pages.admin.jobs.index.tables_solid_queue")} description={t("ui.react.pages.admin.jobs.index.table_role_summary")}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.table")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.usage_operational")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {props.schemaSummary.map((item) => (<TableRow key={item.table}>
                          <TableCell className="font-mono text-xs">{item.table}</TableCell>
                          <TableCell>
                            <div>{item.purpose}</div>
                            <div className="mt-1">
                              {item.available === false ? (<Badge variant="destructive">{t("ui.react.pages.admin.jobs.index.no_available")}</Badge>) : (<Badge variant="secondary">{t("ui.react.pages.admin.jobs.index.available")}</Badge>)}
                            </div>
                          </TableCell>
                        </TableRow>))}
                    </TableBody>
                  </Table>
            </TableSection>
          </TabsContent>

          <TabsContent value="manual" className="space-y-6">
            <TableSection title={t("ui.react.pages.admin.jobs.index.run_job_manually")} description={t("ui.react.pages.admin.jobs.index.select_job_available_app_jobs")}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.job_class")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.queue_2")}</TableHead>
                        <TableHead>{t("ui.react.pages.admin.jobs.index.signature_perform")}</TableHead>
                        <TableHead className="text-right">{t("ui.react.pages.admin.jobs.index.action")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {props.availableJobs.length === 0 && (<TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground">{t("ui.react.pages.admin.jobs.index.no_executable_jobs")}</TableCell>
                        </TableRow>)}
                      {props.availableJobs.map((job) => (<TableRow key={job.className}>
                          <TableCell>
                            <div className="font-medium">{job.className}</div>
                            <p className="text-xs text-muted-foreground">{job.namespace || "Global"}</p>
                          </TableCell>
                          <TableCell>{job.queueName}</TableCell>
                          <TableCell className="font-mono text-xs">{job.performSignature}</TableCell>
                          <TableCell className="text-right">
                            <Button type="button" size="sm" onClick={() => openRunDialog(job)}>
                              <Rocket className="size-4"/>{t("ui.react.pages.admin.jobs.index.run")}</Button>
                          </TableCell>
                        </TableRow>))}
                    </TableBody>
                  </Table>
            </TableSection>
          </TabsContent>
        </Tabs>
      </MainContainer>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("ui.react.pages.admin.jobs.index.run_job")}</DialogTitle>
            <DialogDescription>
              {selectedJob ? t("ui.react.pages.admin.jobs.index.enqueue_job_description", { className: selectedJob.className, queueName: selectedJob.queueName }) : t("ui.admin.jobs.select_job")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="job-arguments">{t("ui.react.pages.admin.jobs.index.arguments_json")}</Label>
            <Input id="job-arguments" value={argumentsJson} onChange={(event) => setArgumentsJson(event.target.value)} placeholder={t("ui.react.pages.admin.jobs.index.arg1_key_value")}/>
            <p className="text-xs text-muted-foreground">{t("ui.react.pages.admin.jobs.index.use_object_json_keyword_args")}</p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t("ui.react.pages.admin.jobs.index.cancel")}</Button>
            <Button type="button" onClick={runJob}>
              <PlayCircle className="size-4"/>{t("ui.react.pages.admin.jobs.index.enqueue_job")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>)
}
